import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Paper,
  Autocomplete,
  Tooltip,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddToDriveIcon from '@mui/icons-material/AddToDrive';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EditIcon from '@mui/icons-material/Edit';
import { toast } from 'react-toastify';
import DriveImportService from '../../../services/DriveImportService';
import StationsService from '../../../services/StationsService';
import palette from '../../../common/styles/palette';
import {
  DrivePreviewItem,
  DrivePreviewResponse,
  DriveConfirmResponse,
  ItemMatchStatus,
  MatchedUser,
} from '../../../types/DriveImport';

export interface StationStudentOption {
  id: number;
  name: string;
  email?: string | null;
  has_debriefing_video_attachment?: boolean;
}

interface DriveBatchImportModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  eventId: number | string;
  eventName?: string;
  stationId: number | string;
  stationName?: string;
  stationStudents?: StationStudentOption[];
}

interface LocalTableItem extends DrivePreviewItem {
  selected: boolean;
  isManualMatch?: boolean;
}

export default function DriveBatchImportModal({
  open,
  onClose,
  onSuccess,
  eventId,
  eventName,
  stationId,
  stationName,
  stationStudents: propStudents,
}: DriveBatchImportModalProps) {
  const [activeStep, setActiveStep] = useState(0);

  // Passo 1: Entrada
  const [folderUrl, setFolderUrl] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [step1Error, setStep1Error] = useState<string | null>(null);

  // Alunos da estação para seleção manual
  const [students, setStudents] = useState<StationStudentOption[]>(propStudents || []);

  // Passo 2: Revisão
  const [previewData, setPreviewData] = useState<DrivePreviewResponse | null>(null);
  const [tableItems, setTableItems] = useState<LocalTableItem[]>([]);
  const [overwrite, setOverwrite] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [step2Error, setStep2Error] = useState<string | null>(null);
  const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null);

  // Passo 3: Resultado
  const [confirmResult, setConfirmResult] = useState<DriveConfirmResponse | null>(null);

  // Carrega alunos da estação se não vierem via props
  useEffect(() => {
    if (propStudents && propStudents.length > 0) {
      setStudents(propStudents);
      return;
    }
    if (open && stationId) {
      StationsService.getUsersStations(stationId)
        .then((res: any) => {
          const list = res?.data?.evaluateds || [];
          const formatted: StationStudentOption[] = list.map((item: any) => ({
            id: Number(item.user_id || item.id),
            name: item.user_name || item.name || 'Aluno',
            email: item.email || null,
            has_debriefing_video_attachment: Boolean(item.has_debriefing_video_attachment),
          }));
          setStudents(formatted);
        })
        .catch(() => {});
    }
  }, [open, stationId, propStudents]);

  // Reseta estados ao fechar
  const handleClose = () => {
    if (analyzing || confirming) return;
    setActiveStep(0);
    setFolderUrl('');
    setAnalyzing(false);
    setStep1Error(null);
    setPreviewData(null);
    setTableItems([]);
    setOverwrite(false);
    setConfirming(false);
    setStep2Error(null);
    setConfirmResult(null);
    setEditingRowIndex(null);
    onClose();
  };

  // ---------------------------------------------------------------------------
  // Passo 1: Analisar pasta
  // ---------------------------------------------------------------------------
  const handleAnalyzeFolder = async () => {
    const trimmed = folderUrl.trim();
    if (!trimmed) {
      setStep1Error('Informe a URL ou o ID da pasta do Google Drive.');
      return;
    }

    setAnalyzing(true);
    setStep1Error(null);

    try {
      const response = await DriveImportService.preview({
        folder_url: trimmed,
        event_id: Number(eventId),
        station_id: Number(stationId),
      });

      const data = response.data;
      setPreviewData(data);

      // Converte itens da API para o estado da tabela com flags de seleção inicial
      const initialItems: LocalTableItem[] = (data.items || []).map((item) => {
        // ready: marcado por padrão; already_exists: desmarcado; unmatched/ambiguous: desmarcado
        const selected = item.status === 'ready';
        return {
          ...item,
          selected,
        };
      });

      setTableItems(initialItems);
      setActiveStep(1);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.Error ||
        err?.response?.data?.error ||
        err?.message ||
        'Não foi possível analisar a pasta informada. Verifique se o link está público e correto.';
      setStep1Error(msg);
    } finally {
      setAnalyzing(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Passo 2: Ações de seleção e matching
  // ---------------------------------------------------------------------------

  // Atualização do switch de sobrescrever
  const handleToggleOverwrite = (newVal: boolean) => {
    setOverwrite(newVal);
    // Se desativar overwrite, desmarca os itens que já possuem anexo
    if (!newVal) {
      setTableItems((prev) =>
        prev.map((item) =>
          item.status === 'already_exists' ? { ...item, selected: false } : item
        )
      );
    }
  };

  // Toggle de seleção de um item
  const handleToggleItem = (index: number) => {
    setTableItems((prev) => {
      const updated = [...prev];
      const target = updated[index];
      const isSelectable =
        target.matched_user !== null &&
        (target.status === 'ready' || (target.status === 'already_exists' && overwrite));

      if (isSelectable) {
        target.selected = !target.selected;
      }
      return updated;
    });
  };

  // Selecionar/desmarcar todos os elegíveis
  const selectableItems = useMemo(() => {
    return tableItems.filter(
      (item) =>
        item.matched_user !== null &&
        (item.status === 'ready' || (item.status === 'already_exists' && overwrite))
    );
  }, [tableItems, overwrite]);

  const allSelectableChecked =
    selectableItems.length > 0 && selectableItems.every((item) => item.selected);
  const someSelectableChecked =
    selectableItems.some((item) => item.selected) && !allSelectableChecked;

  const handleToggleAll = () => {
    const nextState = !allSelectableChecked;
    setTableItems((prev) =>
      prev.map((item) => {
        const isEligible =
          item.matched_user !== null &&
          (item.status === 'ready' || (item.status === 'already_exists' && overwrite));
        return isEligible ? { ...item, selected: nextState } : item;
      })
    );
  };

  // Associação manual de um aluno a um arquivo
  const handleAssignStudent = (index: number, student: StationStudentOption | null) => {
    setTableItems((prev) => {
      const updated = [...prev];
      const current = { ...updated[index] };

      if (!student) {
        // Desassociação
        current.matched_user = null;
        current.status = 'unmatched';
        current.selected = false;
        current.isManualMatch = false;
      } else {
        const hasExisting = Boolean(student.has_debriefing_video_attachment);
        const newStatus: ItemMatchStatus = hasExisting ? 'already_exists' : 'ready';

        current.matched_user = {
          id: student.id,
          name: student.name,
          email: student.email || null,
        };
        current.status = newStatus;
        current.isManualMatch = true;
        // Se ficou pronto ou se já existe com overwrite ligado, marca por padrão
        current.selected = newStatus === 'ready' || (newStatus === 'already_exists' && overwrite);
      }

      updated[index] = current;
      return updated;
    });
    setEditingRowIndex(null);
  };

  // Itens atualmente selecionados para envio
  const selectedToConfirm = useMemo(() => {
    return tableItems.filter((item) => item.selected && item.matched_user);
  }, [tableItems]);

  // Contadores dinâmicos calculados a partir dos tableItems
  const dynamicCounts = useMemo(() => {
    return {
      total: tableItems.length,
      ready: tableItems.filter((i) => i.status === 'ready').length,
      already_exists: tableItems.filter((i) => i.status === 'already_exists').length,
      unmatched: tableItems.filter((i) => i.status === 'unmatched').length,
      ambiguous: tableItems.filter((i) => i.status === 'ambiguous').length,
    };
  }, [tableItems]);

  // Confirmação do lote
  const handleConfirmBatch = async () => {
    if (selectedToConfirm.length === 0) {
      setStep2Error('Selecione pelo menos um vídeo para importar.');
      return;
    }

    setConfirming(true);
    setStep2Error(null);

    const payload = {
      event_id: Number(eventId),
      station_id: Number(stationId),
      overwrite,
      items: selectedToConfirm.map((item) => ({
        drive_file_id: item.drive_file_id,
        user_id: Number(item.matched_user!.id),
        drive_file_name: item.drive_file_name,
      })),
    };

    try {
      const response = await DriveImportService.confirm(payload);
      setConfirmResult(response.data);
      setActiveStep(2);
      toast.success(response.data.message || 'Lote de vídeos enfileirado com sucesso!');
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.Error ||
        err?.response?.data?.error ||
        err?.message ||
        'Erro ao enfileirar o lote de importação.';
      setStep2Error(msg);
    } finally {
      setConfirming(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Helpers de Renderização
  // ---------------------------------------------------------------------------
  const renderStatusBadge = (status: ItemMatchStatus) => {
    switch (status) {
      case 'ready':
        return (
          <Chip
            size="small"
            label="Pronto"
            sx={{
              bgcolor: palette.emeraldSoft,
              color: palette.emeraldDeep,
              fontWeight: 600,
              fontSize: '0.75rem',
            }}
          />
        );
      case 'already_exists':
        return (
          <Chip
            size="small"
            label="Vídeo existente"
            sx={{
              bgcolor: palette.warningSoft,
              color: palette.warning,
              fontWeight: 600,
              fontSize: '0.75rem',
            }}
          />
        );
      case 'ambiguous':
        return (
          <Chip
            size="small"
            label="Ambiguidade"
            sx={{
              bgcolor: palette.dangerSoft,
              color: palette.danger,
              fontWeight: 600,
              fontSize: '0.75rem',
            }}
          />
        );
      case 'unmatched':
      default:
        return (
          <Chip
            size="small"
            label="Não identificado"
            sx={{
              bgcolor: palette.muted,
              color: palette.textMuted,
              fontWeight: 600,
              fontSize: '0.75rem',
            }}
          />
        );
    }
  };

  const renderMatchMethod = (item: LocalTableItem) => {
    if (item.isManualMatch) {
      return (
        <Typography variant="caption" sx={{ color: palette.brand, fontWeight: 600 }}>
          Manual
        </Typography>
      );
    }
    switch (item.match_method) {
      case 'id':
        return (
          <Typography variant="caption" sx={{ color: palette.textMuted }}>
            Por ID
          </Typography>
        );
      case 'name':
        return (
          <Typography variant="caption" sx={{ color: palette.textMuted }}>
            Nome Exato
          </Typography>
        );
      case 'name_partial':
        return (
          <Typography variant="caption" sx={{ color: palette.textMuted }}>
            Nome Parcial
          </Typography>
        );
      default:
        return (
          <Typography variant="caption" sx={{ color: palette.textMuted }}>
            -
          </Typography>
        );
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${palette.border}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <AddToDriveIcon sx={{ color: palette.brand }} />
          <Box>
            <Typography variant="h6" component="div" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
              Importação em Lote: Google Drive
            </Typography>
            <Typography variant="caption" sx={{ color: palette.textMuted }}>
              Estação: <strong>{stationName || stationId}</strong> • Evento:{' '}
              <strong>{eventName || eventId}</strong>
            </Typography>
          </Box>
        </Box>
        <IconButton
          aria-label="fechar"
          onClick={handleClose}
          disabled={analyzing || confirming}
          size="small"
          sx={{ color: palette.textMuted }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box sx={{ width: '100%', pt: 2, px: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          <Step>
            <StepLabel>URL da Pasta</StepLabel>
          </Step>
          <Step>
            <StepLabel>Revisão e Seleção</StepLabel>
          </Step>
          <Step>
            <StepLabel>Conclusão</StepLabel>
          </Step>
        </Stepper>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {/* ================================================================= */}
        {/* PASSO 0: ENTRADA E ANÁLISE                                        */}
        {/* ================================================================= */}
        {activeStep === 0 && (
          <Box sx={{ mt: 1 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              Informe o link da pasta do Google Drive contendo as gravações dos alunos. Os arquivos
              serão analisados automaticamente antes de qualquer transferência.
              <br />
              <strong>Importante:</strong> A pasta deve estar compartilhada com acesso de visualização
              público (<em>Qualquer pessoa com o link</em>).
            </Alert>

            {step1Error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setStep1Error(null)}>
                {step1Error}
              </Alert>
            )}

            <TextField
              autoFocus
              label="URL ou ID da Pasta no Google Drive"
              placeholder="https://drive.google.com/drive/folders/1aBcDeFgHiJkLmNoPqRsTuVwXyZ"
              value={folderUrl}
              onChange={(e) => {
                setFolderUrl(e.target.value);
                if (step1Error) setStep1Error(null);
              }}
              disabled={analyzing}
              fullWidth
              variant="outlined"
              helperText="Formatos aceitos: link completo da pasta ou identificador alfanumérico do Google Drive."
            />
          </Box>
        )}

        {/* ================================================================= */}
        {/* PASSO 1: REVISÃO E SELEÇÃO                                        */}
        {/* ================================================================= */}
        {activeStep === 1 && (
          <Box>
            {/* Contadores e Resumo */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1.5,
                mb: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={`Total de arquivos: ${dynamicCounts.total}`}
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
                <Chip
                  label={`Prontos: ${dynamicCounts.ready}`}
                  sx={{
                    bgcolor: palette.emeraldSoft,
                    color: palette.emeraldDeep,
                    fontWeight: 600,
                  }}
                />
                <Chip
                  label={`Vídeo existente: ${dynamicCounts.already_exists}`}
                  sx={{
                    bgcolor: palette.warningSoft,
                    color: palette.warning,
                    fontWeight: 600,
                  }}
                />
                {dynamicCounts.unmatched > 0 && (
                  <Chip
                    label={`Não identificados: ${dynamicCounts.unmatched}`}
                    sx={{
                      bgcolor: palette.muted,
                      color: palette.textMuted,
                      fontWeight: 600,
                    }}
                  />
                )}
                {dynamicCounts.ambiguous > 0 && (
                  <Chip
                    label={`Ambíguos: ${dynamicCounts.ambiguous}`}
                    sx={{
                      bgcolor: palette.dangerSoft,
                      color: palette.danger,
                      fontWeight: 600,
                    }}
                  />
                )}
              </Box>

              {/* Switch de sobrescrever */}
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={overwrite}
                      onChange={(e) => handleToggleOverwrite(e.target.checked)}
                      color="warning"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Permitir substituição de vídeos existentes
                    </Typography>
                  }
                />
              </Box>
            </Box>

            {overwrite && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Atenção:</strong> A substituição de vídeos está ativada. Arquivos marcados com o
                status <em>Vídeo existente</em> que forem selecionados substituirão a gravação atual
                do aluno.
              </Alert>
            )}

            {step2Error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setStep2Error(null)}>
                {step2Error}
              </Alert>
            )}

            {/* Tabela de Itens */}
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={someSelectableChecked}
                        checked={allSelectableChecked}
                        onChange={handleToggleAll}
                        disabled={selectableItems.length === 0}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Arquivo no Google Drive</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Aluno da Estação</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Identificação</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableItems.map((item, index) => {
                    const isSelectable =
                      item.matched_user !== null &&
                      (item.status === 'ready' || (item.status === 'already_exists' && overwrite));
                    const isEditing = editingRowIndex === index;
                    const needsAssociation =
                      item.status === 'unmatched' || item.status === 'ambiguous';

                    return (
                      <TableRow
                        key={item.drive_file_id || index}
                        hover
                        selected={item.selected}
                        sx={{
                          '&.Mui-selected': { bgcolor: 'rgba(106, 48, 222, 0.08)' },
                          '&.Mui-selected:hover': { bgcolor: 'rgba(106, 48, 222, 0.12)' },
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={item.selected}
                            disabled={!isSelectable}
                            onChange={() => handleToggleItem(index)}
                          />
                        </TableCell>

                        {/* Arquivo no Drive */}
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {item.drive_file_name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: palette.textMuted }}>
                            {item.drive_file_size_formatted}
                          </Typography>
                        </TableCell>

                        {/* Aluno da Estação */}
                        <TableCell sx={{ minWidth: 260 }}>
                          {isEditing || (needsAssociation && !item.matched_user) ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Autocomplete
                                size="small"
                                fullWidth
                                options={students}
                                getOptionLabel={(opt) => `${opt.name} (ID: ${opt.id})`}
                                value={
                                  students.find((s) => s.id === item.matched_user?.id) || null
                                }
                                onChange={(_, newVal) => handleAssignStudent(index, newVal)}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    placeholder="Pesquisar aluno..."
                                    variant="outlined"
                                    autoFocus
                                  />
                                )}
                              />
                              {isEditing && (
                                <IconButton
                                  size="small"
                                  onClick={() => setEditingRowIndex(null)}
                                  sx={{ color: palette.textMuted }}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {item.matched_user?.name}
                                </Typography>
                                {item.matched_user?.email && (
                                  <Typography variant="caption" sx={{ color: palette.textMuted }}>
                                    {item.matched_user.email}
                                  </Typography>
                                )}
                              </Box>
                              <Tooltip title="Alterar aluno vinculado">
                                <IconButton
                                  size="small"
                                  onClick={() => setEditingRowIndex(index)}
                                  sx={{ color: palette.textMuted, ml: 1 }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </TableCell>

                        {/* Método de Identificação */}
                        <TableCell>{renderMatchMethod(item)}</TableCell>

                        {/* Status */}
                        <TableCell>{renderStatusBadge(item.status)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* ================================================================= */}
        {/* PASSO 2: CONCLUSÃO                                                */}
        {/* ================================================================= */}
        {activeStep === 2 && confirmResult && (
          <Box sx={{ py: 2, textAlign: 'center' }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 64, color: palette.emeraldDeep, mb: 1.5 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: palette.text, mb: 1 }}>
              Importação Enfileirada com Sucesso!
            </Typography>
            <Typography variant="body1" sx={{ color: palette.textMuted, maxWidth: 600, mx: 'auto', mb: 3 }}>
              {confirmResult.message}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 3,
                mb: 3,
              }}
            >
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  minWidth: 160,
                  bgcolor: palette.emeraldSoft,
                  borderColor: palette.emeraldDeep,
                }}
              >
                <Typography variant="h4" sx={{ fontWeight: 700, color: palette.emeraldDeep }}>
                  {confirmResult.dispatched_count}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: palette.emeraldDeep }}>
                  Vídeos em Sincronização
                </Typography>
              </Paper>

              {confirmResult.skipped_count > 0 && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    minWidth: 160,
                    bgcolor: palette.warningSoft,
                    borderColor: palette.warning,
                  }}
                >
                  <Typography variant="h4" sx={{ fontWeight: 700, color: palette.warning }}>
                    {confirmResult.skipped_count}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: palette.warning }}>
                    Ignorados (Vídeo existente)
                  </Typography>
                </Paper>
              )}
            </Box>

            <Alert severity="info" sx={{ maxWidth: 700, mx: 'auto', textAlign: 'left' }}>
              Os arquivos estão sendo transferidos pelo servidor diretamente para o armazenamento da
              plataforma. O progresso de cada aluno atualizará automaticamente para{' '}
              <strong>Sincronizando vídeo...</strong> e ficará pronto para debriefing assim que a
              cópia for finalizada.
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${palette.border}`,
          justifyContent: 'space-between',
        }}
      >
        {activeStep === 0 && (
          <>
            <Button onClick={handleClose} disabled={analyzing} color="inherit">
              Cancelar
            </Button>
            <Button
              onClick={handleAnalyzeFolder}
              disabled={analyzing || !folderUrl.trim()}
              variant="contained"
              sx={{
                bgcolor: palette.brand,
                '&:hover': { bgcolor: palette.brandDark },
                color: '#fff',
                minWidth: 150,
              }}
              startIcon={analyzing ? <CircularProgress size={16} color="inherit" /> : <AddToDriveIcon />}
            >
              {analyzing ? 'Analisando...' : 'Analisar Pasta'}
            </Button>
          </>
        )}

        {activeStep === 1 && (
          <>
            <Button
              onClick={() => setActiveStep(0)}
              disabled={confirming}
              startIcon={<ArrowBackIcon />}
              color="inherit"
            >
              Voltar à URL
            </Button>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button onClick={handleClose} disabled={confirming} color="inherit">
                Cancelar
              </Button>
              <Button
                onClick={handleConfirmBatch}
                disabled={confirming || selectedToConfirm.length === 0}
                variant="contained"
                sx={{
                  bgcolor: palette.brand,
                  '&:hover': { bgcolor: palette.brandDark },
                  color: '#fff',
                  minWidth: 180,
                }}
                startIcon={
                  confirming ? <CircularProgress size={16} color="inherit" /> : <AddToDriveIcon />
                }
              >
                {confirming
                  ? 'Confirmando...'
                  : `Confirmar Importação (${selectedToConfirm.length})`}
              </Button>
            </Box>
          </>
        )}

        {activeStep === 2 && (
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              onClick={() => {
                handleClose();
                onSuccess();
              }}
              variant="contained"
              sx={{
                bgcolor: palette.brand,
                '&:hover': { bgcolor: palette.brandDark },
                color: '#fff',
                minWidth: 160,
              }}
            >
              Concluir e Ver Estação
            </Button>
          </Box>
        )}
      </DialogActions>
    </Dialog>
  );
}
